import Scene = Phaser.Scene;
import Sprite = Phaser.GameObjects.Sprite;
import Image = Phaser.GameObjects.Image;
import {Keys} from "@/constants/key";
import {Urls} from "@/constants/urls";
import Group = Phaser.GameObjects.Group;
import TileSprite = Phaser.GameObjects.TileSprite;
import AnimationUtils from "@/utils/animation-utils";
import CU from "./c-u";
import SpineUtils from "@/utils/spine-utils";
import {Settings} from "@/constants/settings";
import {Lang} from "@/store/player/types";
import Container = Phaser.GameObjects.Container;
import Text = Phaser.GameObjects.Text;
import Tween = Phaser.Tweens.Tween;

export default class SpriteUtils {

    public static RED_BORDER: Set<string> = new Set<string>([
        'tm1_swl_ns_evil'
    ]);

    public static BLUE_BORDER: Set<string> = new Set<string>([
        'swl_ns'
    ]);

    public static readonly STUB_IMAGE: string = Keys.CLOUD;

    /** Static Mirrorland flag. The waving one is chosen in SpineUtils. */
    private static staticFlag(texture: string, frame?: string | number): [string, string | number | undefined] {
        if (Settings.LANG_TYPE === Lang.EN && texture === 'obj_1' && frame === 'flag') {
            return ['flag_en', undefined];
        }
        return [texture, frame];
    }

    /**
     * Событие на спрайте-заглушке: скелетная анимация догрузилась, и заглушка прямо сейчас будет
     * заменена на spine-объект (передаётся аргументом) и уничтожена.
     */
    public static readonly SPINE_REPLACED: string = 'spine-replaced';

    private static sizesToSet: Map<Image | Sprite | TileSprite, Sizes> = new Map<Image | Sprite, Sizes>();
    private static hasSizesToSet: boolean = false;

    private static maxSizesToSet: Map<Image | Sprite | TileSprite, Sizes> = new Map<Image | Sprite, Sizes>();
    private static hasMaxSizesToSet: boolean = false;

    public static scale<T extends (Container | Sprite | Image | Text)>(c: T, x?: number, y?: number): T {
        if (!y) {
            y = x;
        }
        return <T>c.setScale(x ? x * CU.S : x, y ? y * CU.S : y);
    }

    public static scaleOriginal<T extends (Container | Sprite | Image | Text)>(c: T, x?: number, y?: number): T {
        return <T>c.setScale(x, y);
    }

    public static addAndPlayAnim(scene: Scene, x: number, y: number, animId: string, arr: Sprite[]): Sprite {
        if (scene.textures.exists(animId)) {

            AnimationUtils.createAnimation(scene, animId);
            let animSprite = scene.add.sprite(x * CU.S, y * CU.S, animId);
            if (animId === 'man_back_idle' || animId === 'woman_back_idle') {
                SpriteUtils.scale(animSprite, 0.78, 0.78);
            } else {
                SpriteUtils.scale(animSprite, 1, 1);
            }
            animSprite.play(animId);

            arr.push(animSprite);
            return animSprite;

        } else {
            let sprite: Sprite = scene.add.sprite(x * CU.S, y * CU.S, SpriteUtils.STUB_IMAGE);
            if (Urls.IMAGES.has(animId)) {
                const finalize = () => {
                    if (!sprite.active) return;
                    AnimationUtils.createAnimation(scene, animId);
                    SpriteUtils.doSetTexture(sprite, animId);
                    if (animId === 'man_back_idle' || animId === 'woman_back_idle') {
                        SpriteUtils.scale(sprite, 0.78, 0.78);
                    } else {
                        SpriteUtils.scale(sprite, 1, 1);
                    }
                    sprite.play(animId);
                    arr.push(sprite);
                };

                // Параллельная загрузка могла уже положить текстуру в кэш — finalize сразу.
                if (scene.textures.exists(animId)) {
                    finalize();
                } else {
                    scene.load.once(`filecomplete-image-${animId}`, finalize);
                    scene.load.image(animId, Urls.IMAGES.get(animId) + '.png');
                    scene.load.start();
                }
            } else {
                console.log('Unknown url for texture: ' + animId);
            }
            return sprite;
        }
    }

    public static createPlaceVisual(
        scene: Scene,
        group: Group,
        x: number,
        y: number,
        depth: number,
        atlasId: string,
        imageId: string,
        animationId?: string,
        shadowScale?: number,
        onLoadComplete?: () => void
    ): any | undefined {
        const texture = atlasId || imageId;
        const frame = atlasId ? imageId : undefined;

        if (SpineUtils.has(texture, frame)) {
            return SpineUtils.addSpine(scene, x, y, texture, frame, {
                animation: animationId,
                scale: shadowScale && shadowScale > 0 ? shadowScale : undefined,
                originX: 0.5,
                originY: 0.5,
                depth,
                group,
                onLoadComplete,
            });
        }

        if (animationId) {
            return undefined;
        }

        return atlasId
            ? SpriteUtils.groupCreate(scene, group, x, y, depth, atlasId, imageId)
            : SpriteUtils.groupCreate(scene, group, x, y, depth, imageId);
    }

    public static hasManagedPlaceVisual(atlasId: string, imageId: string): boolean {
        const texture = atlasId || imageId;
        const frame = atlasId ? imageId : undefined;
        return SpineUtils.has(texture, frame);
    }

    public static addSpriteN(scene: Scene, x: number, y: number,
                            texture: string, frame?: string | number | undefined,
                            scaleX?: number, scaleY?: number): Sprite {
        let res: Sprite = this.doAddSpriteN(scene, x, y, texture, frame);

        if (scaleX || scaleY) {
            SpriteUtils.scale(res, scaleX, scaleY);
        } else {
            SpriteUtils.scale(res, 1, 1);
        }

        return res;
    }

    private static doAddSpriteN(scene: Scene, x: number, y: number, texture: string, frame?: string | number | undefined): Sprite {
        [texture, frame] = SpriteUtils.staticFlag(texture, frame);
        if (scene.textures.exists(texture)) {

            return scene.add.sprite(x, y, texture, frame);

        } else {
            let sprite: Sprite | Image = scene.add.sprite(x, y, SpriteUtils.STUB_IMAGE);
            return <Sprite> SpriteUtils.process(scene, sprite, texture, frame);
        }
    }

    public static addSprite(scene: Scene, x: number, y: number,
                            texture: string, frame?: string | number | undefined,
                            scaleX?: number, scaleY?: number): Sprite {
        let res: Sprite = this.doAddSprite(scene, x, y, texture, frame);

        if (scaleX || scaleY) {
            SpriteUtils.scale(res, scaleX, scaleY);
        } else {
            SpriteUtils.scale(res, 1, 1);
        }

        return res;
    }

    private static doAddSprite(scene: Scene, x: number, y: number, texture: string, frame?: string | number | undefined): Sprite {
        const spine = SpriteUtils.createManagedSpineOrPlaceholder(scene, x, y, texture, frame, true);
        if (spine) {
            return spine as Sprite;
        }

        [texture, frame] = SpriteUtils.staticFlag(texture, frame);
        if (scene.textures.exists(texture)) {

            return scene.add.sprite(x * CU.S, y * CU.S, texture, frame);

        } else {
            let sprite: Sprite | Image = scene.add.sprite(x * CU.S, y * CU.S, SpriteUtils.STUB_IMAGE);
            return <Sprite> SpriteUtils.process(scene, sprite, texture, frame);
        }
    }

    public static makeImage(scene: Scene, options: ImageOptions): Image {
        const image = SpriteUtils.addImage(
            scene,
            options.x,
            options.y,
            options.texture,
            options.frame,
            options.scaleX,
            options.scaleY
        );

        options.depth != null && image.setDepth(options.depth);
        options.origin != null && image.setOrigin(options.origin);
        options.alpha != null && image.setAlpha(options.alpha);

        return image;
    };

    public static addImage(scene: Scene, x: number, y: number, texture: string, frame?: string | number | undefined,
                           scaleX?: number, scaleY?: number, allowSpine: boolean = true): Image {
        let res: Image = this.doAddImage(scene, x, y, texture, frame, allowSpine);

        if (scaleX || scaleY) {
            SpriteUtils.scale(res, scaleX, scaleY);
        } else {
            SpriteUtils.scale(res, 1, 1);
        }

        return res;
    }

    public static doAddImage(scene: Scene, x: number, y: number, texture: string, frame?: string | number | undefined,
                             allowSpine: boolean = true): Image {
        if (!texture || texture.length === 0) {
            throw new Error('texture null');
        }

        const spine = allowSpine && SpriteUtils.createManagedSpineOrPlaceholder(scene, x, y, texture, frame, false);
        if (spine) {
            return spine as Image;
        }

        [texture, frame] = SpriteUtils.staticFlag(texture, frame);
        if (scene.textures.exists(texture)) {

            return scene.add.image(x * CU.S, y * CU.S, texture, frame);

        } else {
            let sprite: Image = scene.add.image(x * CU.S, y * CU.S, SpriteUtils.STUB_IMAGE);
            return <Image> SpriteUtils.process(scene, sprite, texture, frame);
        }
    }

    public static groupCreate(scene: Scene, group: Group, x: number, y: number, depth: number,
                              texture: string, frame: string | undefined = undefined, scaleX?: number, scaleY?: number) {
        let res = this.doGroupCreate(scene, group, x, y, depth, texture, frame);

        if (scaleX || scaleY) {
            SpriteUtils.scale(res, scaleX, scaleY);
        } else {
            SpriteUtils.scale(res, 1, 1);
        }

        return res;
    }

    public static doGroupCreate(scene: Scene, group: Group, x: number, y: number, depth: number, texture: string, frame: string | undefined = undefined) {
        [texture, frame] = SpriteUtils.staticFlag(texture, frame) as [string, string | undefined];
        if (scene.textures.exists(texture)) {

            let res = group.create(x * CU.S, y * CU.S, texture, frame);
            res.setDepth(depth);
            return res;

        } else {
            let sprite = group.create(x * CU.S, y * CU.S, this.STUB_IMAGE);
            sprite.setDepth(depth);
            return SpriteUtils.process(scene, sprite, texture, frame);
        }
    }

    public static setSprite(scene: Scene, sprite: Sprite | Image, texture: string, frame: string | undefined = undefined) {
        [texture, frame] = SpriteUtils.staticFlag(texture, frame) as [string, string | undefined];
        if (scene.textures.exists(texture)) {

            return sprite.setTexture(texture, frame);

        } else {
            return SpriteUtils.process(scene, sprite, texture, frame);
        }
    }

    public static setDispSize(sprite: Sprite | Image, width: number, height: number) {
        if (sprite.texture?.key === SpriteUtils.STUB_IMAGE) {
            SpriteUtils.sizesToSet.set(sprite, new Sizes(width, height));
            SpriteUtils.hasSizesToSet = true;
        } else {
            sprite.setDisplaySize(width, height);
        }
    }

    public static setMaxDispSize(sprite: Sprite | Image, maxWidth: number, maxHeight: number) {
        if (sprite.texture && sprite.texture.key === SpriteUtils.STUB_IMAGE) {
            SpriteUtils.maxSizesToSet.set(sprite, new Sizes(maxWidth, maxHeight));
            SpriteUtils.hasMaxSizesToSet = true;
        } else {
            SpriteUtils.applyMaxDispSize(sprite, maxWidth, maxHeight);
        }
    }

    private static applyMaxDispSize(sprite: Sprite | Image | TileSprite, maxWidth: number, maxHeight: number) {
        const scale = Math.min(maxWidth / sprite.width, maxHeight / sprite.height, 1);
        sprite.setDisplaySize(sprite.width * scale, sprite.height * scale);
    }

    private static doSetTexture(sprite: Sprite | Image | TileSprite, texture: string, frame: string | number | undefined = undefined) {
        sprite.setTexture(texture, frame);
        if (SpriteUtils.hasSizesToSet) {
            let ss = SpriteUtils.sizesToSet.get(sprite);
            if (ss) {
                sprite.setDisplaySize(ss.width, ss.height);
                SpriteUtils.sizesToSet.delete(sprite);
                SpriteUtils.hasSizesToSet = SpriteUtils.sizesToSet.size > 0;
            }
        }
        if (SpriteUtils.hasMaxSizesToSet) {
            let ms = SpriteUtils.maxSizesToSet.get(sprite);
            if (ms) {
                SpriteUtils.applyMaxDispSize(sprite, ms.width, ms.height);
                SpriteUtils.maxSizesToSet.delete(sprite);
                SpriteUtils.hasMaxSizesToSet = SpriteUtils.maxSizesToSet.size > 0;
            }
        }
    }

    private static haveDisabledAtlases = window.location.href.indexOf("disable_") > -1;

    private static process(scene: Scene, sprite: Sprite | Image | TileSprite, texture: string, frame?: string | number | undefined): Sprite | Image | TileSprite {
        try {
            if (frame) {
                if (Urls.ATLASES.has(texture)) {
                    // Та же защита, что и в loadAsync: если параллельно уже загрузили — ставим сразу.
                    if (scene.textures.exists(texture)) {
                        SpriteUtils.doSetTexture(sprite, texture, frame);
                    } else {
                        let path = Urls.ATLASES.get(texture);
                        const isSpriteSheet = typeof frame === 'number';
                        let eventName: string | undefined;

                        if (isSpriteSheet) {
                            scene.load.spritesheet(texture, path + (Urls.ATLASES_WEBP.has(texture) ? '.webp' : '.png'), Urls.FRAME_CONFIGS.get(texture));
                            eventName = `filecomplete-spritesheet-${texture}`;
                        } else if (!this.haveDisabledAtlases || window.location.href.indexOf("disable_" + texture) == -1 ) {
                            scene.load.atlas(texture, path + (Urls.ATLASES_WEBP.has(texture) ? '.webp' : '.png'), path + '.json');
                            eventName = `filecomplete-atlasjson-${texture}`;
                        }

                        if (eventName) {
                            scene.load.once(eventName, () => {
                                if (sprite.active) {
                                    SpriteUtils.doSetTexture(sprite, texture, frame);
                                }
                            });
                        }
                        scene.load.start();
                    }

                } else {
                    console.log('Unknown url for atlas: ' + texture);
                }
            } else {
                if (Urls.IMAGES.has(texture)) {
                    SpriteUtils.loadAsync(scene, texture, Urls.IMAGES.get(texture), sprite);
                } else if (Urls.IMAGES_WEBP.has(texture)) {
                    // Боевые фоны живут только в IMAGES_WEBP по пути assets/bg/<key>.webp.
                    // Если сюда дошли — значит loader-сцена не успела догрузить фон, поэтому подтягиваем сами.
                    SpriteUtils.loadAsync(scene, texture, `assets/bg/${texture}.webp`, sprite);
                } else if (texture && texture.startsWith('p_')) {
                    SpriteUtils.loadAsync(scene, texture, `assets/profile/creatures/${texture.replace('p_', '')}.png`, sprite);
                } else {
                    console.log('Unknown url for texture: ' + texture);
                }
            }
        } catch (e: any) {
            // do nothing
        }

        return sprite;
    }

    private static createManagedSpineOrPlaceholder(
        scene: Scene,
        x: number,
        y: number,
        texture: string,
        frame: string | number | undefined,
        asSprite: boolean
    ): Sprite | Image | undefined {
        if (typeof frame === 'number' || !SpineUtils.has(texture, frame)) {
            return undefined;
        }

        if (SpineUtils.isLoaded(scene, texture, frame)) {
            const loadedSpine = SpineUtils.addSpine(scene, x, y, texture, frame);
            return loadedSpine || undefined;
        }

        const placeholder = asSprite
            ? scene.add.sprite(x * CU.S, y * CU.S, SpriteUtils.STUB_IMAGE)
            : scene.add.image(x * CU.S, y * CU.S, SpriteUtils.STUB_IMAGE);

        SpriteUtils.prepareManagedSpinePlaceholder(placeholder);

        SpineUtils.ensureLoaded(scene, texture, frame, () => {
            SpriteUtils.replaceManagedSpinePlaceholder(scene, placeholder, texture, frame);
        });

        return placeholder;
    }

    private static prepareManagedSpinePlaceholder(placeholder: Sprite | Image): void {
        const state = {visible: true, alpha: 1};
        const originalSetVisible = placeholder.setVisible.bind(placeholder);
        const originalSetAlpha = placeholder.setAlpha.bind(placeholder);

        originalSetVisible(false);

        (placeholder as any).__managedSpineState = state;
        (placeholder as any).setVisible = (value: boolean) => {
            state.visible = value;
            originalSetVisible(false);
            return placeholder;
        };
        (placeholder as any).setAlpha = (value: number) => {
            state.alpha = value;
            originalSetAlpha(value);
            return placeholder;
        };
    }

    private static replaceManagedSpinePlaceholder(
        scene: Scene,
        placeholder: Sprite | Image,
        texture: string,
        frame: string | undefined
    ): void {
        if (!placeholder.active) {
            return;
        }

        const state = (placeholder as any).__managedSpineState ?? {visible: placeholder.visible, alpha: placeholder.alpha};
        const parentContainer = (placeholder as any).parentContainer;
        const index = typeof parentContainer?.getIndex === 'function' ? parentContainer.getIndex(placeholder) : -1;
        const spine = SpineUtils.addSpine(scene, placeholder.x / CU.S, placeholder.y / CU.S, texture, frame, {
            originX: placeholder.originX,
            originY: placeholder.originY,
            depth: placeholder.depth,
        });

        if (!spine) {
            return;
        }

        if (typeof spine.setScale === 'function') {
            spine.setScale(placeholder.scaleX, placeholder.scaleY);
        }
        if (typeof spine.setRotation === 'function') {
            spine.setRotation(placeholder.rotation);
        }
        if (typeof spine.setAngle === 'function') {
            spine.setAngle(placeholder.angle);
        }
        if (typeof spine.setAlpha === 'function') {
            spine.setAlpha(state.alpha);
        }
        if (typeof spine.setVisible === 'function') {
            spine.setVisible(state.visible);
        }

        const size = SpriteUtils.sizesToSet.get(placeholder);
        if (size) {
            spine.setDisplaySize(size.width, size.height);
            SpriteUtils.sizesToSet.delete(placeholder);
            SpriteUtils.hasSizesToSet = SpriteUtils.sizesToSet.size > 0;
        }
        const maxSize = SpriteUtils.maxSizesToSet.get(placeholder);
        if (maxSize) {
            SpriteUtils.applyMaxDispSize(spine, maxSize.width, maxSize.height);
            SpriteUtils.maxSizesToSet.delete(placeholder);
            SpriteUtils.hasMaxSizesToSet = SpriteUtils.maxSizesToSet.size > 0;
        }

        // Keep click/hover behavior when an asynchronously loaded animation replaces
        // its sprite. EventEmitter3 records retain both listener context and once flags.
        if (placeholder.input) {
            if (placeholder.input.customHitArea) {
                spine.setInteractive(placeholder.input.hitArea, placeholder.input.hitAreaCallback);
            } else {
                spine.setInteractive();
            }
            spine.input.enabled = placeholder.input.enabled;
            spine.input.cursor = placeholder.input.cursor;
            if (placeholder.input.draggable) {
                scene.input.setDraggable(spine);
            }
        }
        const inputEvents = ['pointerdown', 'pointerup', 'pointerover', 'pointerout', 'pointermove',
            'pointerupoutside', 'dragstart', 'drag', 'dragend', 'drop'];
        for (const event of inputEvents) {
            const listeners = (placeholder as any)._events?.[event];
            for (const listener of listeners ? (Array.isArray(listeners) ? listeners : [listeners]) : []) {
                spine[listener.once ? 'once' : 'on'](event, listener.fn,
                    listener.context === placeholder ? spine : listener.context);
            }
        }

        if (parentContainer) {
            if (index >= 0 && typeof parentContainer.addAt === 'function') {
                parentContainer.addAt(spine, index);
            } else if (typeof parentContainer.add === 'function') {
                parentContainer.add(spine);
            }
        }

        // Вызывающий код держит ссылку на заглушку, а живой объект теперь другой. Кто эту ссылку
        // хранит (чтобы потом двигать или уничтожить) - должен успеть переставить её на spine,
        // иначе объект останется на экране навсегда и перестанет слушаться владельца.
        placeholder.emit(SpriteUtils.SPINE_REPLACED, spine);

        placeholder.destroy();
    }

    private static loadAsync(scene: Scene, texture: string, path: string | string[] | undefined, sprite: Sprite | Image | TileSprite) {
        // Race: параллельная загрузка могла уже положить текстуру в кэш между exists()-проверкой
        // в process() и этим вызовом. В таком случае load.image() будет no-op (keyExists конфликт),
        // и общий filecomplete-listener никогда не сработает — sprite навсегда останется STUB.
        if (scene.textures.exists(texture)) {
            SpriteUtils.doSetTexture(sprite, texture);
            return;
        }

        // Specific filecomplete-image-<key> + once: сработает ровно для нашего ключа и сам снимется.
        // Раньше слушали общий 'filecomplete' через addListener — listener'ы накапливались и
        // выпускались на каждый файл, что приводило к шуму и потенциальным пропускам по sprite.active.
        scene.load.once(`filecomplete-image-${texture}`, () => {
            if (sprite.active) {
                SpriteUtils.doSetTexture(sprite, texture);
            }
        });

        scene.load.image(texture, path);
        scene.load.start();
    }

//     public static tweenChildren(parent:Group, times?:number[], presets?:NamedPreset[], loop?:boolean,  easings?:Function[]){
//         if(times && presets){
//             parent.presetTimes = times;
//             parent.presets = presets;
//             parent.presetLooped = loop;
//             parent.easings = easings;
//         }
//         SpriteUtils.doTweenChildren(parent);
//     }
//
//     private static doTweenChildren(parent:BasePanel){
//         let presets = parent.presets;
//         let position = parent.presetIndex;
//         if(!parent.presetLooped && position == presets.length-1){
//             return;
//         }
//         position = position % presets.length;
//         let from = presets[position].p;
//         let to = presets[(position + 1) % presets.length].p;
//         let easing = parent.easings? parent.easings[position] : Phaser.Easing.Linear.None;
//         let tweens = SpriteUtils.tweenChildrenFromTo(parent, parent.presetTimes[position], from, to, easing);
//         tweens[0].onComplete.add(function() {
//             parent.presetIndex+=1;
//             SpriteUtils.doTweenChildren(parent);
//         })
//     }
//
//     private static tweenChildrenFromTo(parent:Group, time:number, presetFrom:Preset[], presetTo:Preset[], easing?:Function):Tween[] {
//         let res:Tween[] = [];
//         for (let c in parent.children){
//             if (c instanceof Sprite && c.name){
//                 let from = presetFrom.filter(p => p.spriteId == c.name).shift();
//                 let to = presetTo.filter(p => p.spriteId == c.name).shift();
//                 if(from && to){
//                     res = res.concat(SpriteUtils.tweenFromTo(c, time, from, to, easing));
//                 }
//             }
//         }
//         res.forEach(t => t.timeScale = SpriteUtils.TWEEN_TIME_SCALE);
//         return res;
//     }
//
//     public static TWEEN_TIME_SCALE = 1;
//
//     public static updateTweenData(game:Phaser.Game, timeScale:number) {
//         this.TWEEN_TIME_SCALE = timeScale;
//         let allTweens = game.tweens.getAll();
//         for (var i = 0; i < allTweens.length; i++) {
//             var tween = allTweens[i];
//             tween.timeScale = timeScale;
//         }
//     };
//
//     public static tweenFromTo(sprite:Sprite, time:number, presetFrom:Preset, presetTo:Preset, easing?:Function):Tween[] {
//         var game = sprite.game;
//
//         let SC = G.SC;
//
//         // Создайте новый tween для спрайта
//         var forwardTween = game.add.tween(sprite);
//         sprite.x = presetFrom.x * SC;
//         sprite.y = presetFrom.y * SC;
//         sprite.setOrigin(presetFrom.anchorX, presetFrom.anchorY);
//         sprite.setScale( presetFrom.scaleX * SC, presetFrom.scaleY * SC);
//         sprite.rotation = presetFrom.rotation;
//
//         // Создайте объект с конечными значениями, которые вы хотите достичь
//         var toValues = {
//             x: presetTo.x * SC,
//             y: presetTo.y * SC,
//             rotation: presetTo.rotation
//         };
//
//         let finalEasing = easing || Phaser.Easing.Linear.None;
//         forwardTween.to(toValues, time, finalEasing);
//
//         // Создайте отдельные tween-ы для масштабирования по x и по y
//         var scaleXTweenForward = game.add.tween(sprite.scale);
//         scaleXTweenForward.to({ x: presetTo.scaleX * SC }, time, finalEasing);
//
//         var scaleYTweenForward = game.add.tween(sprite.scale);
//         scaleYTweenForward.to({ y: presetTo.scaleY * SC}, time, finalEasing);
//
//
//         var anchorXTweenForward = game.add.tween(sprite);
//         anchorXTweenForward.to({ originX: presetTo.anchorX }, time, finalEasing);
//
//         var anchorYTweenForward = game.add.tween(sprite);
//         anchorYTweenForward.to({ originY: presetTo.anchorY }, time, finalEasing);
//
//         forwardTween.start();
//         scaleXTweenForward.start();
//         scaleYTweenForward.start();
//         anchorXTweenForward.start();
//         anchorYTweenForward.start();
//
//         return [forwardTween, scaleXTweenForward, scaleYTweenForward, anchorXTweenForward, anchorYTweenForward];
//     }
}
//
// class Preset {
//     public spriteId: string;
//     public x: number;
//     public y: number;
//     public scaleX: number;
//     public scaleY: number;
//     public anchorX: number;
//     public anchorY: number;
//     public rotation: number;
//     public fontSize?: number;
// }
//
// class NamedPreset {
//     public name: string;
//     public p:Preset[];
//
// }

class Sizes {
    public width!: number;
    public height!: number;

    constructor(width: number, height: number) {
        this.width = width;
        this.height = height;
    }
}

export type ImageOptions = {
    x: number;
    y: number;
    texture: string;
    frame?: string | number;
    scaleX?: number;
    scaleY?: number;
    origin?: number;
    depth?: number;
    alpha?: number;
}



